import type { WorkspaceSearchResultType } from "@/pages/workspace/components/content-search/ResultTypeSwitch";

/**
 * The Workspaces page keeps its search in the address (LAR-235), so "Tilbage" from a
 * document opened from the results, and the browser's back button, land on the same
 * search. A visit to the plain /workspaces (the breadcrumb, the sidebar) starts clean.
 */
const SEARCH = "search";
const VIEW = "view";
const ZOOM = "zoom";
const FROM = "from";

export interface WorkspaceSearchUrlState {
  search: string;
  resultType: WorkspaceSearchResultType;
  /** The workspace zoomed into; only meaningful in the documents view. */
  zoomedWorkspaceId: string | null;
  /**
   * The view the zoom was opened from, which "Alle workspaces" returns to. Kept in the
   * address only when it is not the documents view itself, and only while zoomed.
   */
  zoomedFrom?: WorkspaceSearchResultType;
}

const isResultType = (
  value: string | null
): value is WorkspaceSearchResultType =>
  value === "all" || value === "workspaces" || value === "documents";

export const readWorkspaceSearchParams = (
  locationSearch: string
): WorkspaceSearchUrlState => {
  const params = new URLSearchParams(locationSearch);
  const search = params.get(SEARCH)?.trim() ?? "";
  if (!search) {
    return { search: "", resultType: "all", zoomedWorkspaceId: null };
  }
  const view = params.get(VIEW);
  const resultType = isResultType(view) ? view : "all";
  const zoom = params.get(ZOOM);
  const zoomedWorkspaceId = resultType === "documents" && zoom ? zoom : null;
  const from = params.get(FROM);
  return {
    search,
    resultType,
    zoomedWorkspaceId,
    zoomedFrom: zoomedWorkspaceId && isResultType(from) ? from : undefined,
  };
};

/** Writes the state into `params`, leaving out defaults and parameters it does not own alone. */
export const writeWorkspaceSearchParams = (
  params: URLSearchParams,
  { search, resultType, zoomedWorkspaceId, zoomedFrom }: WorkspaceSearchUrlState
): URLSearchParams => {
  params.delete(SEARCH);
  params.delete(VIEW);
  params.delete(ZOOM);
  params.delete(FROM);

  const term = search.trim();
  if (!term) return params;

  params.set(SEARCH, term);
  if (resultType !== "all") params.set(VIEW, resultType);
  if (resultType === "documents" && zoomedWorkspaceId) {
    params.set(ZOOM, zoomedWorkspaceId);
    if (zoomedFrom && zoomedFrom !== "documents") params.set(FROM, zoomedFrom);
  }
  return params;
};
